import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/**
 * Returns true if the error is likely a chunk load failure (e.g. after a new deploy:
 * user still has old index.html, requested chunk URL no longer exists, server returns
 * HTML 404 page → MIME type error).
 */
function isChunkLoadError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err);
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('Loading chunk') ||
    message.includes('Loading CSS chunk')
  );
}

const CHUNK_RELOAD_FLAG = 'bitchill:chunk-reload-attempted';

function canUseSessionStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

function wasChunkReloadAttempted(): boolean {
  if (!canUseSessionStorage()) return false;
  return window.sessionStorage.getItem(CHUNK_RELOAD_FLAG) === '1';
}

function markChunkReloadAttempted(): void {
  if (!canUseSessionStorage()) return;
  window.sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
}

function clearChunkReloadAttempted(): void {
  if (!canUseSessionStorage()) return;
  window.sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
}

/**
 * React.lazy() wrapper that catches chunk load errors (typically after a new deployment)
 * and triggers a full page reload so the user gets the latest index.html and asset URLs.
 * Without this, the user would see a black screen and "Expected a JavaScript module but
 * got MIME type text/html" in the console.
 */
// React.lazy is typed with ComponentType<any>; keep this localized here.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithChunkReload<C extends ComponentType<any>>(
  factory: () => Promise<{ default: C }>,
): LazyExoticComponent<C> {
  return lazy(() =>
    factory()
      .then((module) => {
        // App is healthy after this chunk resolved, allow future recovery reloads.
        clearChunkReloadAttempted();
        return module;
      })
      .catch((err: unknown) => {
        if (isChunkLoadError(err)) {
          if (wasChunkReloadAttempted()) {
            // Avoid infinite reload loops if the app is consistently broken.
            throw err;
          }
          markChunkReloadAttempted();
          window.location.reload();
          return new Promise<never>(() => {});
        }
        throw err;
      }),
  );
}
